/**
 * The shared selection: which people the People screen is showing, in order, and which view they
 * belong to. The person panel imports this for J/K (next and previous person) and never reads the
 * table itself. The table publishes on every change and clears on unmount, so off the People
 * screen the list is empty and `neighbour()` returns null. It also carries what each row shows
 * (days left, district, licence end, reviewed), so the panel repeats the row, never its own guess.
 */
import { useSyncExternalStore } from "react";

/** What a row shows about a person, as the table formatted it. */
export interface RowFacts {
  district?: string;
  /** Days left in the window, as the row prints it ("—" for none). */
  left?: string;
  /** The licence end date, as the row prints it. */
  expires?: string;
  /** A queue case somebody has already worked. */
  done?: boolean;
}

export interface Selection {
  /** The current view id (`lapsed-65`, `event-ce21:2611`, …); "" when People is not mounted. */
  viewId: string;
  /** Person ids in the order the rows are shown (search applied). */
  ids: readonly number[];
  facts: ReadonlyMap<number, RowFacts>;
}

const NO_FACTS: ReadonlyMap<number, RowFacts> = new Map();
const EMPTY: Selection = { viewId: "", ids: [], facts: NO_FACTS };
let current: Selection = EMPTY;
const listeners = new Set<() => void>();

/** Publish the rows now on screen. A no-op when nothing changed, so it is safe to call every render. */
export function setSelection(viewId: string, ids: readonly number[], facts: ReadonlyMap<number, RowFacts> = NO_FACTS): void {
  const same = current.ids.length === ids.length && current.ids.every((id, i) => id === ids[i]);
  if (current.viewId === viewId && same && current.facts === facts) return;
  current = { viewId, ids, facts };
  listeners.forEach((fn) => fn());
}

export function clearSelection(): void {
  setSelection("", []);
}

export function getSelection(): Selection {
  return current;
}

export function subscribeSelection(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** React binding: re-renders when the list or the view changes. */
export function useSelection(): Selection {
  return useSyncExternalStore(subscribeSelection, getSelection, getSelection);
}

/**
 * The person `step` places from `id` (1 = next, -1 = previous), or null at either end, when `id`
 * is not in the list, or off the People screen. It stops at the ends rather than wrapping.
 */
export function neighbour(id: number | null | undefined, step: 1 | -1): number | null {
  if (id == null) return null;
  const at = current.ids.indexOf(id);
  if (at < 0) return null;
  return current.ids[at + step] ?? null;
}

/** What the row for `id` shows, or undefined when that person is not in the list on screen. */
export function useRowFacts(id: number | null): RowFacts | undefined {
  const { facts } = useSelection();
  return id === null ? undefined : facts.get(id);
}
